import { useCallback, useMemo } from 'react'

import { useGetTrackedGamesQuery } from '../../../api/core'
import { useLiveEventsTrackerAccessCheck } from '../../account/hooks/roleHooks'
import { useCurrentUserLanguage } from '../../account/hooks/useCurrentUserLanguage'
import { useFreeLiveEventsGamesSetting } from '../../settings'

export type LiveEventGameAccess = {
  // True when the user's plan doesn't cover this game's live events.
  isGameLocked: (gameId?: string) => boolean
  isLoading: boolean
  // Access couldn't be worked out. Nothing is reported locked then, so callers that can ask the
  // backend instead (the event dialog) should.
  isError: boolean
}

/**
 * Per-game Live Events access, decided the same way as the Live Events Tracker game list:
 * a game is open when the tracked games list puts it in `gamesWithPermission`, or when it
 * is one of the free Live Events games.
 *
 * Only for surfaces with nothing but a game id to go on, like a deep link into the event
 * dialog. Live Event Analytics payloads carry the backend's own `gameAllowed` per game;
 * read that instead.
 *
 * Nothing is reported as locked until the tracked games list is in, so entitled games don't
 * flash a lock on first render. Without the Live Events Tracker role nothing is reported
 * either: those users are stopped by the role gate before any game-level link.
 */
export const useLiveEventGameAccess = (): LiveEventGameAccess => {
  const userLanguage = useCurrentUserLanguage()
  const hasLiveEventsTrackerAccess = useLiveEventsTrackerAccessCheck()
  const {
    data: trackedGames,
    isLoading: isTrackedGamesLoading,
    isError: isTrackedGamesError,
  } = useGetTrackedGamesQuery({ userLanguage }, { skip: !hasLiveEventsTrackerAccess })
  const { data: freeGamesSetting, isLoading: isFreeGamesLoading, isError: isFreeGamesError } = useFreeLiveEventsGamesSetting()

  const accessibleGameIds = useMemo(() => {
    // Without the free games list a free game would read as locked, so lock nothing instead.
    if (!trackedGames || isFreeGamesError) return undefined
    const ids = new Set<string>()
    trackedGames.forEach((trackedGame) => {
      if (trackedGame.permission && trackedGame.game?.id) ids.add(trackedGame.game.id)
    })
    const freeGameIds = freeGamesSetting?.data?.free_live_events_games
    if (Array.isArray(freeGameIds)) {
      freeGameIds.forEach((id) => typeof id === 'string' && ids.add(id))
    }
    return ids
  }, [trackedGames, isFreeGamesError, freeGamesSetting?.data?.free_live_events_games])

  const isLoading = hasLiveEventsTrackerAccess && (isTrackedGamesLoading || isFreeGamesLoading)
  const isError = hasLiveEventsTrackerAccess && (isTrackedGamesError || isFreeGamesError)

  const isGameLocked = useCallback(
    (gameId?: string) => {
      if (!gameId || isLoading || !accessibleGameIds) return false
      return !accessibleGameIds.has(gameId)
    },
    [accessibleGameIds, isLoading]
  )

  return { isGameLocked, isLoading, isError }
}
